import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useOpsConnectCdnService } from '@/api/operations-org-hooks';
import type { OpsServiceDetail } from '@/api/operations';

/**
 * Console-connection status + connect action for an Eagle CDN service.
 *
 * "Connected" means the service's zone carries its OWN per-customer credential
 * (minted account + HMAC on the customer's console entity). "Disconnected" means
 * it still falls back to the shared reseller key and cannot manage its domains
 * under the customer account. The connect button mints a fresh account +
 * credential (a real vendor-side operation) and is idempotent.
 *
 * Renders only for Eagle services (the only managed-HMAC provider today) — the
 * zone's `connected` flag is undefined for every other provider.
 */
export function OpsCdnConnectionCard({ service }: { service: OpsServiceDetail }) {
  const [open, setOpen] = useState(false);
  const connect = useOpsConnectCdnService();

  const isEagle = (service.provider?.name ?? '').toLowerCase() === 'eagle';
  const zone = service.service_zones.find((z) => z.connected !== undefined);
  if (!isEagle || !zone) return null;

  const connected = zone.connected === true;

  const handleConfirm = async () => {
    if (connect.isPending) return;
    try {
      await connect.mutateAsync({ serviceId: service.id });
      setOpen(false);
      connect.reset();
    } catch {
      // Error stays visible via connect.isError; dialog stays open.
    }
  };

  return (
    <Card>
      <CardBody
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--vn-space-4)',
        }}
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <span style={{ fontWeight: 600 }}>Console connection</span>
            <StatusPill tone={connected ? 'success' : 'warning'}>
              {connected ? 'Connected' : 'Disconnected'}
            </StatusPill>
          </div>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {connected
              ? `Customer entity ${zone.uid} has its own account + credential.`
              : `Using the shared reseller key — create a dedicated account + credential for entity ${zone.uid}.`}
          </span>
        </div>

        {!connected && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              connect.reset();
              setOpen(true);
            }}
          >
            Create account &amp; credential
          </Button>
        )}
      </CardBody>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && !connect.isPending) {
            setOpen(false);
            connect.reset();
          }
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Create console account &amp; credential</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', fontSize: 'var(--vn-font-size-sm)' }}>
              <div>
                This mints a dedicated admin account and access credential on the
                customer&apos;s console entity (<strong>{zone.uid}</strong>) so this
                service can manage its own domains. Safe to re-run.
              </div>
              {connect.isError && (
                <Alert tone="danger">Failed to create the account. Please try again.</Alert>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!connect.isPending) {
                  setOpen(false);
                  connect.reset();
                }
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleConfirm} disabled={connect.isPending}>
              {connect.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
